import { findAndReplace } from 'mdast-util-find-and-replace';

/**
 * Workspace file paths written in prose, found and turned into links
 * (spec 095 FR-018 – FR-021).
 *
 * What counts as a path: a token with at least one `/` between non-space
 * segments (`src/app/main.py`, `./docs/plan.md`), or a bare file name with an
 * extension of two to five letters or digits (`README.md`, `notes.txt`). A
 * bare word without either (`README`, `main`) is not a candidate — too many
 * ordinary words would qualify. A leading `./` is dropped; trailing sentence
 * punctuation is not part of the path. Anything with a scheme (`https://…`)
 * is an address, not a path, and is left alone.
 *
 * The remark plugin rewrites matching text into link nodes whose `url` is
 * `workspace-file:<path>`. That scheme is private to `Markdown`, which turns
 * it into an in-app link when the workspace has the file and back into plain
 * text when it does not (FR-019). Text inside code is never touched: the
 * utility visits text nodes only, and `code`/`inlineCode` hold their value
 * directly rather than in a text child (FR-020). Existing links are skipped so
 * a path that is already an anchor's label is not linked twice.
 */

export const WORKSPACE_FILE_SCHEME = 'workspace-file:';

const SEGMENT = String.raw`[\w.@+-]+`;
/*
  A leading `/` is allowed: a run's summary names files the way the sandbox
  sees them, `/workspace/main.go`, and those were the paths that stayed plain
  text while `README.md` beside them became a link. The lookbehind still
  refuses a `/` preceded by a word character, so the tail of an address
  (`example.com/a/b.ts`) is not read as one.
*/
const PATH_WITH_SLASH = String.raw`(?:\./|/)?(?:${SEGMENT}/)+${SEGMENT}`;

/**
 * Where a run's sandbox mounts the workspace (`_WORKSPACE_MOUNT` in the
 * service's `argo.py`). A path under it is a workspace path with that prefix
 * taken off; `/workdir/…` is the task's own scratch volume and is left as it
 * is — the existence check will find no such folder and the text stays plain.
 */
const WORKSPACE_MOUNT = '/workspace/';
const NAME_WITH_EXTENSION = String.raw`(?:\./)?[\w@+-]+(?:\.[\w-]+)*\.[A-Za-z0-9]{2,5}`;

/**
 * One alternation, guarded so a match cannot start inside a word or a scheme
 * and cannot end in sentence punctuation. Built fresh per call: a `g` regex
 * carries `lastIndex` between uses.
 */
function pathPattern(): RegExp {
  return new RegExp(
    String.raw`(?<![\w/:.@-])(${PATH_WITH_SLASH}|${NAME_WITH_EXTENSION})(?![\w/])`,
    'g',
  );
}

/** Strip the `./` prefix and any punctuation a sentence left on the end. */
export function normaliseWorkspacePath(raw: string): string {
  const trimmed = raw.replace(/^\.\//, '').replace(/[.,;:!?)]+$/, '');
  if (trimmed.startsWith(WORKSPACE_MOUNT)) {
    return trimmed.slice(WORKSPACE_MOUNT.length);
  }
  return trimmed.replace(/^\//, '');
}

/**
 * A first segment that names a host rather than a folder: `gitlab.com`,
 * `api.example.io`. Run summaries mention services as often as files, and each
 * one read as a path cost a failed request (spec 127 FR-015).
 */
const HOST_LIKE =
  /^[a-z0-9-]+(?:\.[a-z0-9-]+)*\.(?:com|org|net|io|dev|app|ai|co|cloud|gov|edu|info|me|tech|sh)$/i;

function isCandidate(raw: string): boolean {
  const path = normaliseWorkspacePath(raw);
  if (!path || path.includes('://')) return false;
  // A flag (`--token`) is an argument, never a file.
  if (path.startsWith('-')) return false;
  if (HOST_LIKE.test(path.split('/')[0] ?? '')) return false;
  // A trailing-punctuation strip can leave a bare word; re-check the rule.
  return path.includes('/') || /\.[A-Za-z0-9]{2,5}$/.test(path);
}

/** Every distinct workspace path mentioned in a piece of prose, in order. */
export function findWorkspacePaths(text: string): string[] {
  const seen = new Set<string>();
  // Fenced and inline code are not prose: drop them before matching, the
  // same exclusion the plugin gets for free from the tree.
  const prose = text.replace(/```[\s\S]*?```/g, ' ').replace(/`[^`\n]*`/g, ' ');
  for (const match of prose.matchAll(pathPattern())) {
    const raw = match[1];
    if (!isCandidate(raw)) continue;
    seen.add(normaliseWorkspacePath(raw));
  }
  return [...seen];
}

type Tree = Parameters<typeof findAndReplace>[0];

/** A remark plugin: path-like text becomes a `workspace-file:` link node. */
export function remarkWorkspacePaths() {
  return (tree: Tree) => {
    findAndReplace(
      tree,
      [
        pathPattern(),
        (raw: string) => {
          if (!isCandidate(raw)) return false;
          const path = normaliseWorkspacePath(raw);
          // Keep any punctuation the match swallowed outside the link.
          const tail = raw.slice(raw.replace(/[.,;:!?)]+$/, '').length);
          const link = {
            type: 'link' as const,
            url: `${WORKSPACE_FILE_SCHEME}${path}`,
            children: [
              {
                type: 'text' as const,
                value: raw.slice(0, raw.length - tail.length),
              },
            ],
          };
          return tail ? [link, { type: 'text' as const, value: tail }] : link;
        },
      ],
      { ignore: ['link', 'linkReference'] },
    );
  };
}

/** The path a `workspace-file:` href carries, or `null` for any other href. */
export function workspacePathOf(href: string | undefined): string | null {
  return href?.startsWith(WORKSPACE_FILE_SCHEME)
    ? href.slice(WORKSPACE_FILE_SCHEME.length)
    : null;
}
